import FeatherIcon from "feather-icons-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ModalVideo from "react-modal-video";
import "react-modal-video/css/modal-video.css";


interface ModalMultiPreviewProps {
  isOpen: boolean;
  urls?: string[];
  content?: {
    title?: string;
    content?: string;
  };
  onClose?: () => void;
  typeScreen?: string;
  noImage?: string;
}

const ModalMultiPreviewImage: React.FC<ModalMultiPreviewProps> = ({
  isOpen,
  urls,
  onClose,
  typeScreen,
  noImage
}) => {
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, []);

  const divRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLVideoElement | HTMLImageElement | any>(null);

  const [isShowMore, setIsShowMore] = useState<boolean>(true);
  const [position, setPosition] = useState({ left: 0, width: 0 });

  const [previews, setPreviews] = useState<string[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [index, setIndex] = useState(0);

  const updatePosition = () => {
    if (elementRef.current && divRef.current) {
      const parent = divRef.current;
      const rect = elementRef.current;
      setPosition((_prev) => ({
        left: (parent?.clientWidth - rect?.clientWidth) / 2,
        width: rect?.clientWidth || 0,
      }));
      setIsShowMore((prev) => true);
    }
  };

  const handleElementLoad = useCallback(() => {
    updatePosition();
  }, []);

  const handleNextPreview = (typeNext: string) => {
    if (typeNext === "PREV") {
      setIndex((prev) =>
        prev > 0 ? prev - 1 : Number(previews?.length || 0) - 1
      );
    } else if (typeNext === "NEXT") {
      setIndex((prev) =>
        prev < Number(previews?.length || 0) - 1 ? prev + 1 : 0
      );
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      handleClose();
      return;
    }
    if (event.key === "ArrowLeft") {
      handleNextPreview("PREV");
      return;
    }
    if (event.key === "ArrowRight") {
      handleNextPreview("NEXT");
      return;
    }
  };

  useEffect(() => {
    if (isOpen && divRef.current) {
      divRef.current.focus();
    }
  }, [isOpen, isShowMore, position]);

  useEffect(() => {
    if (previews?.length) {
      setPreview((_prev: any) => previews?.[index || 0]);
    }
  }, [JSON.stringify(previews), index]);

  useEffect(() => {
    setPreviews(urls || []);
    setPreview((_prev: any) => urls?.[0] || null);
  }, [JSON.stringify(urls)]);

  useEffect(() => {
    return () => {
      setIndex((_prev) => 0);
      setPreviews((_prev) => []);
      setPreview((_prev: any) => null);
    };
  }, [isOpen]);

  return (
    <div className="modal-preview">
      <ModalVideo
        channel="custom"
        isOpen={isOpen}
        onClose={handleClose}
        url={preview || ""}
        allowFullScreen={false}
        animationSpeed={300}
        classNames={{
          modalVideoEffect: "modal-video-effect",
          modalVideo: "modal-video",
          modalVideoClose: "modal-video-close",
          modalVideoBody: "modal-video-body",
          modalVideoInner: "modal-video-inner modal-video-inner-img",
          modalVideoIframeWrap:
            "modal-video-movie-wrap m-auto text-center overflow-hidden",
          modalVideoCloseBtn: "modal-video-close-btn  custom-video-close-btn",
        }}
        ratio="4:3"
        {...{
          children: (
            <div
              ref={divRef}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              style={{
                outline: "none",
                maxWidth: "820px",
              }}
            >
              <img
                className="object-fit"
                style={{
                  objectFit: "cover",
                  width: "auto",
                  height: "100%",
                  margin: "auto",
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
                src={preview || ""}
                alt="preview"
                ref={elementRef}
                onLoad={handleElementLoad}
                tabIndex={-1}
                // onError={({ currentTarget }) => {
                //   currentTarget.onerror = null; // prevents looping
                //   if(noImage) {
                //       currentTarget.src = noImage;
                //   }
                // }}
              />

              <div
                style={{
                  position: "absolute",
                  zIndex: "3",
                  left: 0,
                  top: "50%",
                  padding: "8px",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(234, 234, 234, 0.2)",
                  color: "#fff",
                  cursor: "pointer",
                }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleNextPreview("PREV");
                }}
              >
                <FeatherIcon icon="chevron-left" className="icon-dual" />
              </div>
              <div
                style={{
                  position: "absolute",
                  zIndex: "3",
                  right: 0,
                  top: "50%",
                  padding: "8px",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(234, 234, 234, 0.2)",
                  color: "#fff",
                  cursor: "pointer",
                }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleNextPreview("NEXT");
                }}
              >
                <FeatherIcon icon="chevron-right" className="icon-dual" />
              </div>
              <div
                style={{
                  position: "absolute",
                  zIndex: "3",
                  left: "50%",
                  top: 5,
                  transform: "translateX(-50%)",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "15px",
                  padding: "0px 5px",
                  backgroundColor: "rgba(234, 234, 234, 0.3)",
                  backdropFilter: "blur(5px)",
                }}
              >
                {Number(index) + 1}/{previews?.length}
              </div>
            </div>
          ),
        }}
      />
    </div>
  );
};

export default React.memo(ModalMultiPreviewImage);
